import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildHotspotExplorerSearch,
    buildHotspotDetailPath,
    buildStatePath,
    getCachedHotspotExplorerStats,
    getCachedSortedHotspots,
    getCountryLabel,
    getHotspotExplorerHeroImage,
    getHotspotExplorerPage,
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";
import {usePageTitle} from "../../../helpers/usePageTitle";

export function CountryHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const resolvedHotspotExplorerIndex = hotspotExplorerIndex!;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const currentPage = getHotspotExplorerPage(searchParams);
    const hotspots = useMemo(() => resolvedHotspotExplorerIndex.hotspotsByCountryCode.get(countryCode) ?? [], [countryCode, resolvedHotspotExplorerIndex]);
    const regionBounds = hotspots[0]?.regionData?.country?.bounds ?? undefined;
    const visibleHotspots = useMemo(
        () => getCachedSortedHotspots(hotspots, sortMode),
        [hotspots, sortMode]
    );
    const stateGroups = resolvedHotspotExplorerIndex.stateGroupsByCountryCode.get(countryCode) ?? [];
    const countryLabel = hotspots[0]?.countryLabel || getCountryLabel(countryCode);

    usePageTitle(countryLabel, 'Hotspot Explorer');

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const setPage = (page: number) => {
        const nextSearch = buildHotspotExplorerSearch(searchParams, {page});
        navigate(`${location.pathname}${nextSearch}`, {replace: true});
    }

    const openState = (stateProvince: string) => {
        navigate(`${buildStatePath(countryCode, stateProvince)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'country', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={countryLabel}
            intro={`Hotspots you've visited in ${countryLabel}.`}
            backLabel="Back to Countries"
            onBack={() => navigate(`/hotspot-explorer${buildHotspotExplorerSearch(searchParams, {page: 1})}`)}
            stats={getCachedHotspotExplorerStats(visibleHotspots, 'Counties')}
            panelHeading="By State/Province"
            groups={stateGroups}
            activeGroupCode={undefined}
            onSelectGroup={openState}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            currentPage={currentPage}
            onPageChange={setPage}
            onOpenHotspot={openHotspot}
        />
    );
}
